<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
  scrollAmount: { type: Number },
  sliceKey: { type: Number },
  sliceObj: { type: Object },
  numImages: { type: Number },
  imgWidth: { type: Number },
});

const emit = defineEmits(['move']);

// Refs
const rangeSlider = ref(null);
const rangeThumb = ref(null);

// Reactive data
const isDragging = ref(false);
const startValue = ref(0);
const endValue = ref(null);
const startDragX = ref(null);

// New properties for div positioning
const thumbPosition = ref(0);
const pathLeft = ref(0);
const pathWidth = ref(0);
const sliderWidth = ref(0);

// Computed properties
const totalParentWidth = computed(() => {
  return props.imgWidth * props.numImages;
});

const viewportWidthPercent = computed(() => {
  if (!sliderWidth.value || !totalParentWidth.value) return 20;
  return (sliderWidth.value / totalParentWidth.value) * 100;
});

// Methods
const getSliderWidth = () => {
  if (rangeSlider.value) {
    const rect = rangeSlider.value.getBoundingClientRect();
    sliderWidth.value = rect.width;
  }
};

const updateSliderPositions = async () => {
  await nextTick();
  getSliderWidth();

  if (!props.sliceObj || !props.sliceObj.states || !sliderWidth.value) return;

  const scrollPercent = (props.scrollAmount / totalParentWidth.value) * 100;

  // Calculate thumb position (ensure it stays within bounds)
  thumbPosition.value = Math.min(
    Math.max(scrollPercent, 0),
    100 - viewportWidthPercent.value,
  );

  // Update path based on slice states
  if (props.sliceObj.states) {
    const firstState = Object.values(props.sliceObj.states)[0];
    if (firstState) {
      const { numImages } = firstState;
      const startPercent
        = (firstState.start.phaseLocation / (numImages - 1)) * 100;
      const endPercent = (firstState.end.phaseLocation / (numImages - 1)) * 100;

      pathLeft.value = startPercent;
      pathWidth.value = endPercent - startPercent;
    }
  }

  startValue.value = thumbPosition.value;
  endValue.value = thumbPosition.value + viewportWidthPercent.value;
};

const moveScale = (dx, ignoreThrottle = false) => {
  emit('move', dx, ignoreThrottle);
};

// handleClick
const handleClick = (event) => {
  if (isDragging.value) return;

  const rect = rangeSlider.value.getBoundingClientRect();
  const thumbRect = rangeThumb.value.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const thumbWidth = thumbRect.width;
  const adjustedMouseX = mouseX - thumbWidth / 2;

  const positionPercent = Math.max(
    0,
    Math.min((adjustedMouseX / rect.width) * 100, 100),
  );

  if (positionPercent < 0 || positionPercent > 100) {
    return;
  }

  const targetScrollAmount = (positionPercent / 100) * totalParentWidth.value;
  moveScale(targetScrollAmount, true);
};

const handleMouseMove = (event) => {
  if (!isDragging.value) return;

  const dx = event.clientX - startDragX.value;
  const rect = rangeSlider.value.getBoundingClientRect();

  const deltaPercent = (dx / rect.width) * 100;
  const newPosition = thumbPosition.value + deltaPercent;

  if (newPosition < 0 || newPosition > 100 - viewportWidthPercent.value) {
    return;
  }

  const targetScrollAmount = (newPosition / 100) * totalParentWidth.value;
  moveScale(targetScrollAmount);
  startDragX.value = event.clientX;
};

const handleMouseUp = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
};

const handleMouseDown = (event) => {
  startDragX.value = event.clientX;
  isDragging.value = true;

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

// Lifecycle
onMounted(() => {
  updateSliderPositions();
});

// Watchers
watch(() => props.scrollAmount, updateSliderPositions);
watch(() => props.numImages, updateSliderPositions);
watch(() => props.sliceObj, updateSliderPositions, { deep: true });
watch(() => props.imgWidth, updateSliderPositions);
</script>

<template>
  <div class="px-2 simple-panel rounded-none">
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
    <div ref="rangeSlider" class="range-slider" @click="handleClick">
      <div class="range-slider-track" />
      <div
        ref="rangeThumb"
        class="range-slider-thumb"
        :style="{
          left: `${thumbPosition}%`,
          width: `${viewportWidthPercent}%`,
        }"
        @mousedown="handleMouseDown"
      />

      <div class="range-slider-handler" :style="{ left: `${pathLeft}%` }" />

      <div
        class="range-slider-handler"
        :style="{ left: `${pathLeft + pathWidth}%`, marginLeft: '-4px' }"
      />

      <div
        class="range-slider-path"
        :style="{ left: `${pathLeft}%`, width: `${pathWidth}%` }"
      />
    </div>
  </div>
</template>

<style scoped>
.range-slider {
  position: relative;
  height: 35px;
  width: 100%;
}

.range-slider-track {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 8px;
  background-color: var(--md-text-secondary);
  transform: translateY(-50%);
}

.range-slider-path {
  position: absolute;
  top: 50%;
  height: 8px;
  background-color: var(--md-primary);
  transform: translateY(-50%);
  z-index: 5;
}

.range-slider-thumb {
  position: absolute;
  top: 50%;
  height: 30px;
  border-radius: 0.75rem;
  background-color: var(--md-primary-hover);
  transform: translateY(-50%);
  border: 2px solid var(--md-text-primary);
  cursor: pointer;
  z-index: 10;
  transition: border-radius 0.3s ease;
}

.range-slider-thumb:hover {
  border-radius: var(--container-radius);
}

.range-slider-handler {
  position: absolute;
  top: 50%;
  width: 4px;
  height: 30px;
  background-color: var(--md-primary);
  transform: translateY(-50%);
  border-radius: 8px;
}
</style>
